import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";
import { homeDe } from "@/lib/home";
import { destinoSeguro } from "@/lib/retorno";
import { ScenarioProvider } from "@/context/ScenarioContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import SitesList from "@/pages/SitesList";
import SiteDetail from "@/pages/SiteDetail";
import Users from "@/pages/Users";
import Consola from "@/pages/Consola";
import ConsolaV3 from "@/pages/ConsolaV3";
import CentroV3 from "@/pages/CentroV3";
import Bitacora from "@/pages/Bitacora";
import BitacoraOperativa from "@/pages/BitacoraOperativa";
import SolicitudIA from "@/pages/SolicitudIA";
import SaludTecnica from "@/pages/SaludTecnica";
import Pendientes from "@/pages/Pendientes";
import Programados from "@/pages/Programados";
import CambiarPassword from "@/pages/CambiarPassword";
import Organizaciones from "@/pages/Organizaciones";
import CredencialesWs101 from "@/pages/CredencialesWs101";
import OrganizacionDetalle from "@/pages/OrganizacionDetalle";

// GC92-A · destino de retorno tras el login (regla única en `lib/retorno.js`).
function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        Cargando…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace
      state={{ from: destinoSeguro(location.pathname) }} />;
  }
  // Gate D: credencial temporal -> sólo el flujo de definir contraseña (el backend ya lo impone).
  if (user.must_change_password) return <Navigate to="/cambiar-password" replace />;
  return children;
}

function ProtectedBare({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// CONSOLA-03 (RBAC-004): guard de RUTA con la MISMA regla que oculta el menú. Declarativo en la
// ruta (no por efecto) para que la URL directa nunca monte una superficie de administración
// interna. La autorización efectiva sigue siendo del backend.
function SoloSupervisor({ children }) {
  const { user } = useAuth();
  if (!capacidades(user).supervisor) return <Navigate to="/consola-v3" replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={homeDe(user)} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <ScenarioProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/cambiar-password"
            element={
              <ProtectedBare>
                <CambiarPassword />
              </ProtectedBare>
            }
          />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/" element={<Home />} />
            <Route path="/consola" element={<Navigate to="/consola-v3" replace />} />
            <Route path="/consola-legacy" element={<Consola />} />
            <Route path="/consola-v3" element={<ConsolaV3 />} />
            <Route path="/centro-v3" element={<CentroV3 />} />
            <Route path="/centro-v3/:sitioId" element={<CentroV3 />} />
            <Route path="/bitacora" element={<Bitacora />} />
            <Route path="/bitacora-operativa" element={<BitacoraOperativa />} />
            <Route path="/solicitud/:id" element={<SolicitudIA />} />
            <Route path="/salud" element={<SaludTecnica />} />
            <Route path="/sitios" element={<SitesList />} />
            <Route path="/sitios/:id" element={<SiteDetail />} />
            <Route path="/pendientes" element={<SoloSupervisor><Pendientes /></SoloSupervisor>} />
            <Route path="/programados" element={<Programados />} />
            <Route path="/usuarios" element={<SoloSupervisor><Users /></SoloSupervisor>} />
            <Route path="/ws101-credenciales" element={<SoloSupervisor><CredencialesWs101 /></SoloSupervisor>} />
            <Route path="/organizaciones" element={<SoloSupervisor><Organizaciones /></SoloSupervisor>} />
            <Route path="/organizaciones/:oid" element={<SoloSupervisor><OrganizacionDetalle /></SoloSupervisor>} />
          </Route>
          <Route path="*" element={<Navigate to="/consola-v3" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton duration={3000} visibleToasts={2} />
      </ScenarioProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
